import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Markets from './pages/Markets';
import Screener from './pages/Screener';
import Analysis from './pages/Analysis';
import Watchlist from './pages/Watchlist';
import Portfolio from './pages/Portfolio';
import Alerts from './pages/Alerts';
import AIHistory from './pages/AIHistory';
import StrategyHistory from './pages/StrategyHistory';
import AssetDetail from './pages/AssetDetail';
import NewsCalendar from './pages/NewsCalendar';
import Subscription from './pages/Subscription';
import Landing from './pages/Landing';
import Admin from './pages/Admin';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ConfirmEmailChange from './pages/ConfirmEmailChange';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Settings from './pages/Settings';
import TradingDashboard from './pages/TradingDashboard';
import TradingManualAnalysis from './pages/TradingManualAnalysis';
import TradingTradeReview from './pages/TradingTradeReview';
import TradingInsights from './pages/TradingInsights';
import TradingSkillEditor from './pages/TradingSkillEditor';
import TradingPerformance from './pages/TradingPerformance';
import TradingSettings from './pages/TradingSettings';
import BankerSettings from './pages/BankerSettings';
import BankerLeads from './pages/BankerLeads';
import Orders from './pages/trading/Orders';
import BankerMonitor from './pages/BankerMonitor';
import TradingProviderTest from './pages/TradingProviderTest';
import ForexPairs from './pages/ForexPairs';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeHub from './pages/KnowledgeHub';
import VideoPlayer from './pages/VideoPlayer';
import ExpertChat from './pages/ExpertChat';
import ExpertStrategiesPage from './pages/ExpertStrategiesPage';
import Releases from './pages/Releases';
import McpSettings from './pages/McpSettings';
import PersonaSettings from './pages/PersonaSettings';
import McpPage from './pages/McpPage';
import KnowledgeArticle from './pages/KnowledgeArticle';
import RbacUsers from './pages/admin/RbacUsers';
import RbacUserDetailPage from './pages/admin/RbacUserDetail';
import RbacAuditLog from './pages/admin/RbacAuditLog';
import RbacRoles from './pages/admin/RbacRoles';
import BranchJoin from './pages/BranchJoin';
import SurveyPage from './pages/SurveyPage';
import AppointmentsDashboardPage from './pages/appointments/AppointmentsDashboardPage';
import BookAppointmentPage from './pages/appointments/BookAppointmentPage';
import BookingConfirmationPage from './pages/appointments/BookingConfirmationPage';
import TaskMonitor from './pages/admin/TaskMonitor';
import SupportAdmin from './pages/admin/SupportAdmin';
import AnnouncementsAdmin from './pages/admin/AnnouncementsAdmin';
import AdminAppointments from './pages/admin/AdminAppointments';
import AdminGlossary from './pages/admin/AdminGlossary';
import ServicesStatus from './pages/admin/ServicesStatus';
import PerformanceDashboard from './pages/admin/PerformanceDashboard';
import AdminKycReview from './pages/admin/AdminKycReview';
import AdminContentApprovals from './pages/admin/AdminContentApprovals';
import AdminOrganisations from './pages/admin/Organisations';
import Exceptions from './pages/admin/Exceptions';
import KycCompliance from './pages/KycCompliance';
import GlossaryPage from './pages/GlossaryPage';
import GlossaryTermPage from './pages/GlossaryTermPage';
import SupportTickets from './pages/support/SupportTickets';
import SupportTicketDetail from './pages/support/SupportTicketDetail';
import { PermissionGate } from './components/PermissionGate';
import { ExpertChatProvider } from './contexts/ExpertChatContext';
import { ActiveSpeakerProvider } from './contexts/ActiveSpeakerContext';
import { GlossaryProvider } from './contexts/GlossaryContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { BrandingProvider } from './contexts/BrandingContext';
import ChatButton from './components/expert/ChatButton';
import ChatDrawer from './components/expert/ChatDrawer';
import AnalystBubble from './components/expert/AnalystBubble';
import DisclaimerModal from './components/DisclaimerModal';
import DisclaimerFooter from './components/DisclaimerFooter';
import Faq from './pages/Faq';
import OrgMembers from './pages/org/Members';
import OrgBilling from './pages/org/Billing';
import OrgSignalQueue from './pages/org/SignalQueue';
import OrgMcpKeys from './pages/org/McpKeys';
import OrgBrandingPage from './pages/org/Branding';
import OrgKnowledgeHub from './pages/org/KnowledgeHub';
import OrgDomains from './pages/org/Domains';
import FspProfilePage from './pages/org/FspProfile';
import FspRepresentatives from './pages/org/FspRepresentatives';
import FspMandates from './pages/org/FspMandates';
// WatchlistSignals merged into Watchlist.tsx — redirect old route below
import IPOCalendar from './pages/IPOCalendar';
import UpgradePage from './pages/UpgradePage';
import PaymentCallback from './pages/PaymentCallback';
import BankerBillingResult from './pages/BankerBillingResult';
import { BankerPaywall } from './components/BankerPaywall';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Wrapper for authenticated redirect (redirects logged-in users away from login/register)
const AuthRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Wrapper for protected routes (redirects unauthenticated users to landing page)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }
  return <>{children}</>;
};

// Loading screen component
const LoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
  </div>
);

// Main routes component
const AppRoutes: React.FC = () => {
  const { isLoading, isAuthenticated, disclaimerAcknowledged, setDisclaimerAcknowledged } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show FAIS disclaimer modal before app content for authenticated users who haven't acknowledged
  if (isAuthenticated && !disclaimerAcknowledged) {
    return (
      <DisclaimerModal
        onAcknowledged={() => setDisclaimerAcknowledged(true)}
      />
    );
  }

  return (
    <Routes>
      {/* Auth routes - no layout */}
      <Route
        path="/login"
        element={
          <AuthRedirect>
            <Login />
          </AuthRedirect>
        }
      />
      <Route
        path="/register"
        element={
          <AuthRedirect>
            <Register />
          </AuthRedirect>
        }
      />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Main app routes - protected, with layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/markets"
        element={
          <ProtectedRoute>
            <Layout>
              <Markets />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/screener"
        element={
          <ProtectedRoute>
            <Layout>
              <Screener />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analysis"
        element={
          <ProtectedRoute>
            <Layout>
              <Analysis />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            <Layout>
              <Watchlist />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Old signals route redirects to merged watchlist */}
      <Route path="/markets/watchlist-signals" element={<Navigate to="/watchlist" replace />} />
      <Route
        path="/markets/ipo"
        element={
          <ProtectedRoute>
            <Layout>
              <BankerPaywall>
                <IPOCalendar />
              </BankerPaywall>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <Layout>
              <Portfolio />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <Layout>
              <Alerts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-history"
        element={
          <ProtectedRoute>
            <Layout>
              <AIHistory />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/strategy-history"
        element={
          <ProtectedRoute>
            <Layout>
              <StrategyHistory />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute>
            <Layout>
              <NewsCalendar />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forex-pairs"
        element={
          <ProtectedRoute>
            <Layout>
              <ForexPairs />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge"
        element={
          <ProtectedRoute>
            <Layout>
              <KnowledgeHub />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge/articles/:slug"
        element={
          <ProtectedRoute>
            <Layout>
              <KnowledgeArticle />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <VideoPlayer />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/asset/:symbol"
        element={
          <ProtectedRoute>
            <Layout>
              <AssetDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Layout>
              <Subscription />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <Layout>
              <Subscription />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/result"
        element={
          <ProtectedRoute>
            <Layout>
              <BankerBillingResult />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin page */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout>
              <Admin />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* RBAC Admin pages — require admin:user-manage or admin:audit-read */}
      <Route
        path="/admin/access-control/users"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:user-manage" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <RbacUsers />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/access-control/users/:userId"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:user-manage" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <RbacUserDetailPage />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/access-control/audit"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:audit-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <RbacAuditLog />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/access-control/roles"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <RbacRoles />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/background-tasks"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <TaskMonitor />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <SupportAdmin />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <AnnouncementsAdmin />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Admin Appointments — v8-7-2 */}
      <Route
        path="/admin/appointments"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <AdminAppointments />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* Admin Glossary — v9-4-1 */}
      <Route
        path="/admin/knowledge/glossary"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <AdminGlossary />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Services Status — v11-1-1 */}
      <Route
        path="/admin/services"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <ServicesStatus />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Performance Dashboard */}
      <Route
        path="/admin/performance"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <PerformanceDashboard />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* KYC Review — kyc-1-1 */}
      <Route
        path="/admin/kyc"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <AdminKycReview />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Content approvals (v43-1-4) — review openclaw agent drafts */}
      <Route
        path="/admin/content-approvals"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <AdminContentApprovals />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin Organisations — b2b-1-1 */}
      <Route
        path="/admin/organisations"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <AdminOrganisations />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/exceptions"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionGate permission="admin:config-read" fallback={<div className="p-8 text-slate-400">Access denied.</div>}>
                <Exceptions />
              </PermissionGate>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Trading Analyst pages */}
      <Route
        path="/trading"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/analyze"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingManualAnalysis />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/insights"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingInsights />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/skills"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingSkillEditor />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/performance"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingPerformance />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/trades"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingTradeReview />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingSettings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/test-provider"
        element={
          <ProtectedRoute>
            <Layout>
              <TradingProviderTest />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/banker/monitor"
        element={
          <ProtectedRoute>
            <Layout>
              <BankerPaywall>
                <BankerMonitor />
              </BankerPaywall>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/banker/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <BankerPaywall>
                <BankerSettings />
              </BankerPaywall>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/banker/leads"
        element={
          <ProtectedRoute>
            <Layout>
              <BankerPaywall>
                <BankerLeads />
              </BankerPaywall>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/orders"
        element={
          <ProtectedRoute>
            <Layout>
              <Orders />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Booking — Branch Appointments */}
      <Route
        path="/trading/appointments"
        element={
          <ProtectedRoute>
            <Layout>
              <AppointmentsDashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/appointments/book"
        element={
          <ProtectedRoute>
            <Layout>
              <BookAppointmentPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/trading/appointments/confirmation/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <BookingConfirmationPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Expert Chat full-page view */}
      <Route
        path="/trading/expert"
        element={
          <ProtectedRoute>
            <Layout>
              <ExpertChat />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Expert Strategy Library */}
      <Route
        path="/trading/strategies"
        element={
          <ProtectedRoute>
            <Layout>
              <ExpertStrategiesPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Support tickets */}
      <Route
        path="/support/tickets"
        element={
          <ProtectedRoute>
            <Layout>
              <SupportTickets />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/tickets/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SupportTicketDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Settings page */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* KYC Compliance — user identity verification (kyc-1-1) */}
      <Route
        path="/settings/kyc"
        element={
          <ProtectedRoute>
            <Layout>
              <div className="max-w-3xl mx-auto p-6">
                <KycCompliance />
              </div>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* MCP Settings — Premium API key management */}
      <Route
        path="/settings/mcp"
        element={
          <ProtectedRoute>
            <Layout>
              <McpSettings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Persona Bank — admin only (v19-3-2) */}
      <Route
        path="/settings/personas"
        element={
          <ProtectedRoute>
            <Layout>
              <PersonaSettings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Financial Glossary — inside app layout for authenticated users (v9-4-1) */}
      <Route
        path="/knowledge/glossary"
        element={
          <ProtectedRoute>
            <Layout>
              <GlossaryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge/glossary/:slug"
        element={
          <ProtectedRoute>
            <Layout>
              <GlossaryTermPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org settings — member management (b2b-2-1) */}
      <Route
        path="/org/settings/members"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgMembers />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org billing dashboard — Finance Manager (b2b-2-2) */}
      <Route
        path="/org/billing"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgBilling />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org signal queue — Lead Analyst review (b2b-2-3) */}
      <Route
        path="/org/signals/queue"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgSignalQueue />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org MCP API Keys — org admin (b2b-1-3) */}
      <Route
        path="/org/settings/mcp-keys"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgMcpKeys />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org Branding — org admin (b2b-3-1) */}
      <Route
        path="/org/settings/branding"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgBrandingPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org Custom Domains — org admin / devops_sre (b2b-4-1) */}
      <Route
        path="/org/settings/domains"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgDomains />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* FSP Licence Profile — org admin (v31-1-10) */}
      <Route
        path="/org/fsp-profile"
        element={
          <ProtectedRoute>
            <Layout>
              <FspProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/org/fsp-profile/representatives"
        element={
          <ProtectedRoute>
            <Layout>
              <FspRepresentatives />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/org/fsp-profile/mandates"
        element={
          <ProtectedRoute>
            <Layout>
              <FspMandates />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org Knowledge Hub — org admin content management (b2b-3-2) */}
      <Route
        path="/org/knowledge"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgKnowledgeHub />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Branch join — standalone full-screen, outside Layout, no auth required */}
      <Route path="/branch/join/:token" element={<BranchJoin />} />

      {/* Satisfaction survey — standalone full-screen, no auth required (v8-7-3) */}
      <Route path="/branch/survey/:token" element={<SurveyPage />} />

      {/* Public pages */}
      <Route path="/welcome" element={<Landing />} />
      <Route path="/releases" element={<Releases />} />
      <Route path="/mcp" element={<McpPage />} />
      <Route path="/faq" element={<ProtectedRoute><Layout><Faq /></Layout></ProtectedRoute>} />

      {/* Subscription upgrade & payment callback (v28-3-2) */}
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <UpgradePage />
          </ProtectedRoute>
        }
      />
      {/* PayStack redirects here — no auth required */}
      <Route path="/payment/callback" element={<PaymentCallback />} />

      {/* Catch all - redirect to landing page */}
      <Route path="*" element={<Navigate to="/welcome" replace />} />
    </Routes>
  );
};

// Hide the Expert Chat (Market Analyst) overlay during branch/meeting sessions —
// the meeting room has its own Investment Banker chat and they must not run together.
const GlobalChat: React.FC = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/branch/')) return null;
  return (
    <>
      <AnalystBubble />
      <ChatButton />
      <ChatDrawer />
    </>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PermissionProvider>
        <SubscriptionProvider>
          <PreferencesProvider>
            <WebSocketProvider>
              <BrandingProvider>
              <ActiveSpeakerProvider>
              <ExpertChatProvider>
              <GlossaryProvider>
                <Router>
                  <AppRoutes />
                  <GlobalChat />
                  <Toaster
                    position="bottom-center"
                    toastOptions={{
                      style: {
                        background: '#1e293b',
                        color: '#e2e8f0',
                        border: '1px solid #334155',
                        fontSize: '0.875rem',
                      },
                      duration: 2000,
                    }}
                  />
                </Router>
              </GlossaryProvider>
              </ExpertChatProvider>
              </ActiveSpeakerProvider>
              </BrandingProvider>
            </WebSocketProvider>
          </PreferencesProvider>
        </SubscriptionProvider>
        </PermissionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
